import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { agoraSP } from "@/lib/date";
import { PautaDetailModal, type Pauta, STATUS_COLOR } from "./Pautas";
import { useQuadro } from "@/hooks/useQuadro";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";

// Etapas e rótulos vêm do quadro do cliente (useQuadro). O mapa fixo antigo,
// com "Roteiro", saiu em out/26.

const REDE_ICON: Record<string, string> = {
  instagram: "", tiktok: "", youtube: "▶️", linkedin: "", twitter: "𝕏", multiplas: "",
};

// Cores por rede social — visualmente distintas (matching REDE_INFO em Pautas.tsx)
const REDE_COLOR: Record<string, { color: string; bg: string; label: string }> = {
  instagram: { color: "#BE185D", bg: "#FDE8F5", label: "Instagram" },
  tiktok:    { color: "#065F46", bg: "#E6FBF4", label: "TikTok" },
  youtube:   { color: "#991B1B", bg: "#FEE2E2", label: "YouTube" },
  linkedin:  { color: "#1E40AF", bg: "#DBEAFE", label: "LinkedIn" },
  twitter:   { color: "#0C4A6E", bg: "#E0F2FE", label: "Twitter/X" },
  multiplas: { color: "#6B21A8", bg: "#F3E8FF", label: "Múltiplas" },
};
function redeInfo(rede: string | null | undefined) {
  if (!rede) return { color: "#374151", bg: "#F3F4F6" };
  return REDE_COLOR[rede] ?? { color: "#374151", bg: "#F3F4F6" };
}

// Colunas que indicam "já passou de ideação/sugestão" — usadas pelo filtro "só aprovados"
const COLUNAS_APROVADAS = new Set([
  "revisao", "producao_criacao", "revisao_design", "ajuste", "aprovado", "agendado", "publicado",
]);

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Calendario() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;

  // Etapas do Calendário = colunas do kanban do cliente (mesma fonte e mesmas
  // cores). Enquanto a config não chega, não mostra o quadro padrão.
  const { etapasPauta, carregado: quadroCarregado, rotulo } = useQuadro(clientId);
  const COLS = useMemo(() => (quadroCarregado ? etapasPauta : []) as any[], [etapasPauta, quadroCarregado]);
  const colunaInfoMap = useMemo(() => {
    const m: Record<string, { label: string; color: string; bg: string; dot: string }> = {};
    for (const c of COLS) {
      const color = STATUS_COLOR[c.id] ?? c.color ?? "#888";
      m[c.id] = { label: c.label, color, bg: color + "1A", dot: color };
    }
    return m;
  }, [COLS]);
  // Etapa fora do quadro do cliente: aparece com o nome e o aviso, nunca some.
  // Só marca "fora do quadro" depois que o quadro do cliente chegou.
  const ci = (col: string) => colunaInfoMap[col] ?? (quadroCarregado
    ? { label: `${rotulo(col)} (fora do quadro)`, color: "#B42318", bg: "#FEF3F2", dot: "#B42318" }
    : { label: rotulo(col), color: "#888", bg: "#EDEBE6", dot: "#888" });

  const hoje = agoraSP(); // RN-001: hoje no fuso de São Paulo
  const [viewYear, setViewYear]   = useState(hoje.ano);
  const [viewMonth, setViewMonth] = useState(hoje.mes);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [filtroColuna, setFiltroColuna] = useState<string>("todas");
  const [viewMode, setViewMode] = useState<"calendar" | "lista">("calendar");
  // HU Thainá: cor por rede em vez de status + filtro de aprovados
  const [colorMode, setColorMode]     = useState<"status" | "rede">("rede");
  // RN-CAL: o Calendário Editorial reflete TODAS as pautas do kanban (qualquer
  // coluna) posicionadas por data_prevista. Por isso o filtro "Só aprovadas"
  // começa DESLIGADO — segue disponível como estreitamento opcional.
  const [apenasAprovadas, setApenasAprovadas] = useState<boolean>(false);
  const [detalhe, setDetalhe] = useState<Pauta | null>(null); // HU-012

  const { data: pautas = [], isLoading, refetch } = trpc.pautas.list.useQuery(
    { clientId },
    { enabled: !!clientId }
  );
  const deleteMutation = trpc.pautas.delete.useMutation({ onSuccess: () => refetch() });
  const hardDeleteMutation = trpc.pautas.hardDelete.useMutation({ onSuccess: () => refetch() });
  const updateMutation = trpc.pautas.update.useMutation({ onSuccess: () => refetch() });

  // HU-011: reagendamento por drag-and-drop
  function handleDragEnd(result: DropResult) {
    if (!result.destination) return;
    const novaData = result.destination.droppableId;
    const origem = result.source.droppableId;
    if (novaData === origem) return;
    updateMutation.mutate({ id: Number(result.draggableId), dataPrevista: novaData });
  }

  // Agrupar pautas por data (YYYY-MM-DD)
  const pautasByDate = useMemo(() => {
    const map: Record<string, typeof pautas> = {};
    for (const p of pautas) {
      if (!p.dataPrevista) continue;
      const date = p.dataPrevista.substring(0, 10);
      if (!map[date]) map[date] = [];
      map[date].push(p);
    }
    return map;
  }, [pautas]);

  // Pautas sem data para o lado
  const semData = useMemo(
    () => pautas.filter(p => !p.dataPrevista),
    [pautas]
  );

  const daysInMonth   = getDaysInMonth(viewYear, viewMonth);
  const firstDayOfWeek = getFirstDayOfMonth(viewYear, viewMonth);
  const totalCells    = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;
  const todayStr      = `${hoje.ano}-${String(hoje.mes+1).padStart(2,"0")}-${String(hoje.dia).padStart(2,"0")}`;

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
    setSelectedDay(null);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
    setSelectedDay(null);
  }

  function dateStr(day: number) {
    return `${viewYear}-${String(viewMonth + 1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
  }

  const selectedPautas = selectedDay
    ? (pautasByDate[selectedDay] ?? []).filter(p => (filtroColuna === "todas" || p.coluna === filtroColuna) && (!apenasAprovadas || COLUNAS_APROVADAS.has(p.coluna)))
    : [];

  // Total de pautas com data neste mês
  const pautasMes = useMemo(() => {
    const prefix = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}`;
    return pautas.filter(p => p.dataPrevista?.startsWith(prefix));
  }, [pautas, viewYear, viewMonth]);

  return (
    <div className="h-full flex flex-col bg-[var(--solar-bg)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-4 shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-3">
          <div>
            <h1 className="font-bold text-lg text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Calendário
            </h1>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">
              {pautasMes.length} {pautasMes.length === 1 ? "pauta" : "pautas"} em {MESES[viewMonth]}
            </p>
          </div>

          {/* Navegação mês */}
          <div className="flex items-center gap-3">
            <button
              onClick={prevMonth}
              className="w-8 h-8 rounded-[10px] flex items-center justify-center text-[var(--solar-ink)] hover:bg-[var(--solar-bg)] border border-[var(--solar-line)] shadow-[var(--elev-1)] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => { setViewYear(hoje.ano); setViewMonth(hoje.mes); setSelectedDay(null); }}
              className="min-w-[130px] text-center font-bold text-sm text-[var(--solar-ink)] px-4 py-1.5 rounded-[10px] border border-[var(--solar-line)] shadow-[var(--elev-1)] hover:bg-[var(--solar-bg)] transition-colors"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              {MESES[viewMonth]} {viewYear}
            </button>
            <button
              onClick={nextMonth}
              className="w-8 h-8 rounded-[10px] flex items-center justify-center text-[var(--solar-ink)] hover:bg-[var(--solar-bg)] border border-[var(--solar-line)] shadow-[var(--elev-1)] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap gap-y-2">
            {/* HU-010: toggle de visualização */}
            <div className="flex items-center bg-[var(--solar-bg)] rounded-[10px] p-1 border border-[var(--solar-line)]">
              <button
                onClick={() => setViewMode("calendar")}
                className="px-2.5 py-1.5 rounded-md transition-all"
                style={{
                  background: viewMode === "calendar" ? "white" : "transparent",
                  color: viewMode === "calendar" ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: viewMode === "calendar" ? "var(--elev-1)" : "none",
                }}
                title="Visualização mensal"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("lista")}
                className="px-2.5 py-1.5 rounded-md transition-all"
                style={{
                  background: viewMode === "lista" ? "white" : "transparent",
                  color: viewMode === "lista" ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: viewMode === "lista" ? "var(--elev-1)" : "none",
                }}
                title="Visualização em lista"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Toggle cor: status | rede */}
            <div className="flex items-center gap-1 bg-[var(--solar-bg)] rounded-[10px] p-1 border border-[var(--solar-line)]">
              <button
                onClick={() => setColorMode("rede")}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  background: colorMode === "rede" ? "white" : "transparent",
                  color: colorMode === "rede" ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: colorMode === "rede" ? "var(--elev-1)" : "none",
                }}
                title="Cor do evento por rede social"
              >
                Cor: Rede
              </button>
              <button
                onClick={() => setColorMode("status")}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  background: colorMode === "status" ? "white" : "transparent",
                  color: colorMode === "status" ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: colorMode === "status" ? "var(--elev-1)" : "none",
                }}
                title="Cor do evento por status (ideação, em produção, etc)"
              >
                Status
              </button>
            </div>

            {/* Toggle: apenas aprovadas */}
            <label className="flex items-center gap-1.5 bg-[var(--solar-bg)] rounded-[10px] border border-[var(--solar-line)] px-2.5 py-1.5 cursor-pointer hover:bg-[var(--solar-line)] transition-colors" title="Mostrar somente pautas aprovadas (revisão+): esconde ideação e roteiro">
              <input
                type="checkbox"
                checked={apenasAprovadas}
                onChange={e => setApenasAprovadas(e.target.checked)}
                className="w-3.5 h-3.5 accent-[var(--brand)]"
              />
              <span className="text-xs font-medium text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
                Só aprovadas
              </span>
            </label>

            {/* Filtro coluna */}
            <div className="flex items-center gap-1 bg-[var(--solar-bg)] rounded-[10px] border border-[var(--solar-line)] p-1 overflow-x-auto max-w-xl">
              <button
                onClick={() => setFiltroColuna("todas")}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  background: filtroColuna === "todas" ? "white" : "transparent",
                  color: filtroColuna === "todas" ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: filtroColuna === "todas" ? "var(--elev-1)" : "none",
                }}
              >
                Todos
              </button>
              {COLS.map((c: any) => {
                const info = ci(c.id);
                return (
                <button
                  key={c.id}
                  onClick={() => setFiltroColuna(c.id)}
                  className="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    background: filtroColuna === c.id ? "white" : "transparent",
                    color: filtroColuna === c.id ? info.color : "var(--solar-mute)",
                    boxShadow: filtroColuna === c.id ? "var(--elev-1)" : "none",
                  }}
                >
                  {info.label}
                </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* HU-010: vista de lista */}
        {viewMode === "lista" ? (
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--solar-ink)] animate-spin" />
              </div>
            ) : (() => {
              const pautasFiltradas = pautas
                .filter(p => p.dataPrevista && (filtroColuna === "todas" || p.coluna === filtroColuna))
                .sort((a, b) => (a.dataPrevista ?? "").localeCompare(b.dataPrevista ?? ""));

              if (pautasFiltradas.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center h-40 text-[var(--solar-mute)] text-sm gap-2">
                    <svg className="w-8 h-8 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Nenhuma pauta com data para este mês
                  </div>
                );
              }

              // Group by date
              const byDate: Record<string, typeof pautasFiltradas> = {};
              for (const p of pautasFiltradas) {
                const d = p.dataPrevista!.slice(0, 10);
                if (!byDate[d]) byDate[d] = [];
                byDate[d].push(p);
              }

              return (
                <div className="space-y-4 max-w-3xl mx-auto">
                  {Object.entries(byDate).map(([date, items]) => {
                    const isToday = date === todayStr;
                    const dateLabel = new Date(date + "T12:00:00").toLocaleDateString("pt-BR", {
                      weekday: "long", day: "numeric", month: "long"
                    });
                    return (
                      <div key={date}>
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className="text-xs font-bold capitalize px-3 py-1 rounded-full border"
                            style={{
                              background: isToday ? "var(--solar-ink)" : "var(--solar-bg)",
                              color: isToday ? "white" : "var(--ink-2)",
                              borderColor: isToday ? "var(--solar-ink)" : "var(--solar-line)",
                              fontFamily: "Poppins, sans-serif",
                            }}
                          >
                            {isToday ? "Hoje" : dateLabel}
                          </div>
                          <div className="flex-1 h-px bg-[var(--solar-line)]" />
                          <span className="text-xs text-[var(--solar-mute)]">{items.length}</span>
                        </div>
                        <div className="space-y-2">
                          {items.map(p => {
                            const info = ci(p.coluna);
                            return (
                              <div
                                key={p.id}
                                onClick={() => setDetalhe(p as Pauta)}
                                className="bg-white rounded-[14px] border border-[var(--solar-line)] shadow-[var(--elev-1)] px-4 py-3 flex items-center gap-4 hover:shadow-[var(--elev-2)] hover:border-[var(--solar-line)] transition-all cursor-pointer"
                              >
                                <span className="text-lg shrink-0">{REDE_ICON[p.rede] ?? ""}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-[var(--solar-ink)] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>
                                    {p.titulo}
                                  </p>
                                  {(p as any).linhaEditorial && (
                                    <p className="text-xs text-[var(--ink-2)] font-medium">{(p as any).linhaEditorial}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span
                                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                    style={{ background: info.bg, color: info.color }}
                                  >
                                    {info.label}
                                  </span>
                                  <span className="text-xs text-[var(--solar-mute)] capitalize">{p.rede}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        ) : (

        /* ── Calendar view ── */
        <><div className="flex-1 flex flex-col overflow-hidden p-4">
          {/* Cabeçalho dias da semana */}
          <div className="grid grid-cols-7 mb-1">
            {DIAS_SEMANA.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-[var(--solar-mute)] py-2" style={{ fontFamily: "Poppins, sans-serif" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Grade do calendário */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--solar-ink)] animate-spin" />
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex-1 grid grid-cols-7 gap-px bg-[var(--solar-line)] rounded-[14px] overflow-hidden border border-[var(--solar-line)]">
              {Array.from({ length: totalCells }, (_, i) => {
                const dayNum = i - firstDayOfWeek + 1;
                const isValid = dayNum >= 1 && dayNum <= daysInMonth;
                const dStr = isValid ? dateStr(dayNum) : "";
                const isToday = dStr === todayStr;
                const isSelected = dStr === selectedDay;
                const dayPautas = dStr ? (pautasByDate[dStr] ?? []).filter(p => (filtroColuna === "todas" || p.coluna === filtroColuna) && (!apenasAprovadas || COLUNAS_APROVADAS.has(p.coluna))) : [];
                const hasEvents = dayPautas.length > 0;

                if (!isValid) {
                  return <div key={i} className="bg-[var(--solar-bg)] min-h-[80px]" />;
                }

                return (
                  <Droppable droppableId={dStr} key={i}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        onClick={() => hasEvents && setSelectedDay(isSelected ? null : dStr)}
                        className="flex flex-col p-1.5 min-h-[80px] transition-colors"
                        style={{
                          background: snapshot.isDraggingOver ? "var(--brand-soft)" : isSelected ? "var(--solar-bg)" : isToday ? "var(--brand-soft)" : "white",
                          cursor: hasEvents ? "pointer" : "default",
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              background: isToday ? "var(--solar-ink)" : "transparent",
                              color: isToday ? "white" : "var(--solar-ink)",
                            }}
                          >
                            {dayNum}
                          </span>
                          {hasEvents && (
                            <span className="text-[10px] font-semibold text-[var(--ink-2)] bg-[var(--solar-bg)] border border-[var(--solar-line)] px-1.5 py-0.5 rounded-full" style={{ fontFamily: "Poppins, sans-serif" }}>
                              {dayPautas.length}
                            </span>
                          )}
                        </div>

                        {/* Event chips — arrastáveis (HU-011) */}
                        <div className="flex flex-col gap-0.5 flex-1">
                          {dayPautas.slice(0, 3).map((p, idx) => {
                            const info = colorMode === "rede" ? redeInfo(p.rede) : (ci(p.coluna));
                            return (
                              <Draggable key={p.id} draggableId={String(p.id)} index={idx}>
                                {(drag, dragSnap) => (
                                  <div
                                    ref={drag.innerRef}
                                    {...drag.draggableProps}
                                    {...drag.dragHandleProps}
                                    onClick={e => { e.stopPropagation(); setDetalhe(p as Pauta); }}
                                    className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-semibold truncate"
                                    style={{
                                      background: info.bg, color: info.color,
                                      ...drag.draggableProps.style,
                                      opacity: dragSnap.isDragging ? 0.9 : 1,
                                      boxShadow: dragSnap.isDragging ? "var(--elev-2)" : undefined,
                                    }}
                                  >
                                    <span className="text-[8px]">{REDE_ICON[p.rede] ?? ""}</span>
                                    <span className="truncate" style={{ fontFamily: "Poppins, sans-serif" }}>{p.titulo}</span>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {dayPautas.length > 3 && (
                            <span className="text-[9px] text-[var(--solar-mute)] font-medium pl-1" style={{ fontFamily: "Poppins, sans-serif" }}>
                              +{dayPautas.length - 3} mais
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
            </DragDropContext>
          )}
        </div>

        {/* Painel lateral */}
        <div className="w-72 border-l border-[var(--solar-line)] bg-white flex flex-col shrink-0 overflow-hidden">
          {selectedDay ? (
            <>
              {/* Header dia selecionado */}
              <div className="px-4 py-4 border-b border-[var(--solar-line)] shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--solar-mute)]" style={{ fontFamily: "Poppins, sans-serif" }}>
                      {new Date(selectedDay + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long" })}
                    </p>
                    <p className="font-bold text-[var(--solar-ink)] text-sm" style={{ fontFamily: "Poppins, sans-serif" }}>
                      {new Date(selectedDay + "T12:00:00").toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-[var(--solar-mute)] hover:text-[var(--ink-2)] transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-[var(--solar-mute)] mt-1" style={{ fontFamily: "Poppins, sans-serif" }}>
                  {selectedPautas.length} {selectedPautas.length === 1 ? "pauta" : "pautas"}
                </p>
              </div>

              {/* Lista de pautas do dia */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                {selectedPautas.map(p => {
                  const info = colorMode === "rede" ? redeInfo(p.rede) : (ci(p.coluna));
                  const colInfo = ci(p.coluna);
                  return (
                    <div
                      key={p.id}
                      onClick={() => setDetalhe(p as Pauta)}
                      className="rounded-[14px] border p-3 space-y-2 transition-all shadow-[var(--elev-1)] hover:shadow-[var(--elev-2)] cursor-pointer"
                      style={{ borderColor: info.color + "44", background: info.bg + "88" }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg shrink-0">{REDE_ICON[p.rede] ?? ""}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[var(--solar-ink)] leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>
                            {p.titulo}
                          </p>
                          {p.descricao && (
                            <p className="text-[10px] text-[var(--solar-mute)] mt-0.5 line-clamp-2">{p.descricao}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: colInfo.bg, color: colInfo.color, fontFamily: "Poppins, sans-serif" }}
                        >
                          {colInfo.label}
                        </span>
                        <div className="flex gap-1 flex-wrap justify-end">
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                            style={{ background: "var(--solar-bg)", color: "var(--ink-2)" }}
                          >
                            {p.rede}
                          </span>
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                            style={{ background: "var(--solar-bg)", color: "var(--ink-2)" }}
                          >
                            {p.formato}
                          </span>
                        </div>
                      </div>
                      {p.horarioPostagem && (
                        <div className="flex items-center gap-1.5 text-[10px] text-[var(--solar-mute)]">
                          <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="truncate">{p.horarioPostagem}</span>
                        </div>
                      )}
                      {p.responsavel && (
                        <p className="text-[10px] text-[var(--solar-mute)]">{p.responsavel}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {/* Painel padrão: resumo do mês */}
              <div className="px-4 py-4 border-b border-[var(--solar-line)] shrink-0">
                <p className="font-bold text-[var(--solar-ink)] text-sm" style={{ fontFamily: "Poppins, sans-serif" }}>
                  Resumo de {MESES[viewMonth]}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {/* Distribuição por status */}
                <div>
                  <p className="text-[10px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>
                    Por status
                  </p>
                  <div className="space-y-2">
                    {[...COLS.map((c: any) => c.id),
                      // etapas fora do quadro do cliente, para a soma fechar com o total do mês
                      ...Array.from(new Set(pautasMes.map(p => p.coluna))).filter(id => !colunaInfoMap[id])].map((col: string) => {
                      const count = pautasMes.filter(p => p.coluna === col).length;
                      if (count === 0) return null;
                      const info = ci(col);
                      return (
                        <div key={col} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: info.dot }} />
                          <span className="text-xs text-[var(--ink-2)] flex-1" style={{ fontFamily: "Poppins, sans-serif" }}>{info.label}</span>
                          <span className="text-xs font-bold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{count}</span>
                          <div className="w-16 h-1.5 bg-[var(--solar-bg)] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(count / pautasMes.length) * 100}%`, background: info.dot }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Distribuição por rede */}
                <div className="pt-2 border-t border-[var(--solar-line)]">
                  <p className="text-[10px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-2 mt-2" style={{ fontFamily: "Poppins, sans-serif" }}>
                    Por rede
                  </p>
                  <div className="space-y-2">
                    {Object.entries(REDE_COLOR).map(([rede, info]) => {
                      const count = pautasMes.filter(p => p.rede === rede).length;
                      if (count === 0) return null;
                      return (
                        <div key={rede} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: info.color }} />
                          <span className="text-xs text-[var(--ink-2)] flex-1" style={{ fontFamily: "Poppins, sans-serif" }}>{info.label}</span>
                          <span className="text-xs font-bold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{count}</span>
                          <div className="w-16 h-1.5 bg-[var(--solar-bg)] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(count / pautasMes.length) * 100}%`, background: info.color }} />
                          </div>
                        </div>
                      );
                    })}
                    {pautasMes.filter(p => !p.rede || !REDE_COLOR[p.rede]).length > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-[var(--solar-mute)]" />
                        <span className="text-xs text-[var(--solar-mute)] flex-1 italic" style={{ fontFamily: "Poppins, sans-serif" }}>Outras / sem rede</span>
                        <span className="text-xs font-bold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
                          {pautasMes.filter(p => !p.rede || !REDE_COLOR[p.rede]).length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div></>
        )}
      </div>

      {/* HU-012: modal de detalhe da pauta */}
      {detalhe && (
        <PautaDetailModal
          key={detalhe.id}
          pauta={detalhe}
          onClose={() => setDetalhe(null)}
          onSave={() => refetch()}
          onOpenRelated={p => setDetalhe(p)}
          onDelete={id => { deleteMutation.mutate({ id }); setDetalhe(null); }}
          onHardDelete={id => { hardDeleteMutation.mutate({ id }); setDetalhe(null); }}
        />
      )}
    </div>
  );
}
